import React from "react";

export type SpacingToken =
  "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

type AlignValue = "start" | "center" | "end" | "stretch" | "baseline";
type JustifyValue = "start" | "center" | "end" | "between" | "around";

interface LayoutProps {
  gap?: SpacingToken;
  align?: AlignValue;
  justify?: JustifyValue;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const ALIGN: Record<AlignValue, React.CSSProperties["alignItems"]> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
  baseline: "baseline",
};

const JUSTIFY: Record<JustifyValue, React.CSSProperties["justifyContent"]> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
  around: "space-around",
};

function gapVar(gap?: SpacingToken): string | undefined {
  return gap ? `var(--spacing-${gap})` : undefined;
}

/** Vertical flex column. Default for sections, forms, lists. */
export const Stack: React.FC<LayoutProps> = ({
  gap,
  align,
  justify,
  className,
  style,
  children,
}) => (
  <div
    className={className}
    style={{
      display: "flex",
      flexDirection: "column",
      gap: gapVar(gap),
      alignItems: align ? ALIGN[align] : undefined,
      justifyContent: justify ? JUSTIFY[justify] : undefined,
      ...style,
    }}
  >
    {children}
  </div>
);

/** Horizontal flex row, no-wrap. Default for toolbars, button groups. */
export const Inline: React.FC<LayoutProps> = ({
  gap,
  align = "center",
  justify,
  className,
  style,
  children,
}) => (
  <div
    className={className}
    style={{
      display: "flex",
      flexDirection: "row",
      gap: gapVar(gap),
      alignItems: ALIGN[align],
      justifyContent: justify ? JUSTIFY[justify] : undefined,
      flexWrap: "nowrap",
      ...style,
    }}
  >
    {children}
  </div>
);

/** Horizontal flex row, wraps. Default for tag/chip clusters. */
export const Cluster: React.FC<LayoutProps> = ({
  gap,
  align = "center",
  justify,
  className,
  style,
  children,
}) => (
  <div
    className={className}
    style={{
      display: "flex",
      flexDirection: "row",
      gap: gapVar(gap),
      alignItems: ALIGN[align],
      justifyContent: justify ? JUSTIFY[justify] : undefined,
      flexWrap: "wrap",
      ...style,
    }}
  >
    {children}
  </div>
);
